import { Check, ChevronRight, AlertCircle, ShieldAlert } from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { WIZARD_STEPS, CURRENCY_SYMBOLS } from './types';

interface WizardSidebarProps {
  currentStep: number;
  highestCompletedStep: number;
  onStepNavigation: (step: number) => void;
  completedAnswers: number;
  totalQuestions: number;
  /** Map of step number -> whether validation passes (true = valid, false = has errors) */
  stepValidity?: Record<number, boolean>;
  /** Annual spend key for spend score display */
  annualSpend?: string;
  /** Risk level: 'low' | 'medium' | 'high' or undefined if not calculated */
  riskLevel?: string;
  /** Risk score as percentage (0-100) */
  riskScore?: number;
  /** Supplier's trading currency — the spend bands are quoted in it. */
  currency?: string;
}

/**
 * Left sidebar navigation panel for the Supplier Onboarding Wizard.
 * Displays step buttons with checkmarks for completed steps, warning icons
 * for visited-but-incomplete steps, active highlighting, and locked state
 * for steps not yet reached.
 */
export default function WizardSidebar({
  currentStep,
  highestCompletedStep,
  onStepNavigation,
  completedAnswers,
  totalQuestions,
  annualSpend,
  riskLevel,
  riskScore,
  stepValidity,
  currency,
}: WizardSidebarProps) {
  return (
    <div className="w-80 bg-gray-50 border-r p-6">
      <div className="space-y-2">
        {WIZARD_STEPS.map((step) => {
          const Icon = step.icon;
          const isActive = currentStep === step.id;
          const isVisited = step.id <= highestCompletedStep;
          const isAccessible = step.id <= highestCompletedStep + 1;
          const isLocked = !isAccessible;
          // A step is truly completed only if it was visited AND passes validation
          const isValid = stepValidity ? stepValidity[step.id] !== false : true;
          const isCompleted = isVisited && isValid;
          const isIncomplete = isVisited && !isValid;

          return (
            <button
              key={step.id}
              onClick={() => { if (isAccessible) onStepNavigation(step.id); }}
              disabled={isLocked}
              aria-disabled={isLocked}
              className={`w-full flex items-center gap-3 p-4 rounded-lg transition-all text-left
                ${isLocked
                  ? 'opacity-50 cursor-not-allowed pointer-events-none'
                  : isActive
                  ? 'bg-primary text-primary-foreground shadow-md'
                  : isCompleted
                    ? 'bg-green-50 text-green-800 hover:bg-green-100'
                    : isIncomplete
                      ? 'bg-amber-50 text-amber-800 hover:bg-amber-100'
                      : 'bg-white hover:bg-gray-100 text-gray-600'
                }`}
            >
              <div className={`flex items-center justify-center w-10 h-10 rounded-full
                ${isActive
                  ? 'bg-primary-foreground/20'
                  : isCompleted
                    ? 'bg-green-200'
                    : isIncomplete
                      ? 'bg-amber-200'
                      : 'bg-gray-200'
                }`}>
                {isCompleted ? (
                  <Check className="h-5 w-5 text-green-600" />
                ) : isIncomplete ? (
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                ) : (
                  <Icon className={`h-5 w-5 ${isActive ? 'text-primary-foreground' : 'text-gray-500'}`} />
                )}
              </div>
              <div className="flex-1">
                <div className={`text-sm font-semibold ${isActive ? '' : isCompleted ? 'text-green-800' : isIncomplete ? 'text-amber-800' : ''}`}>
                  {step.label}
                </div>
                <div className={`text-xs ${isActive ? 'text-primary-foreground/80' : isIncomplete ? 'text-amber-600' : 'text-gray-400'}`}>
                  {step.key === 'group' && 'Setup supplier group'}
                  {step.key === 'details' && (isIncomplete ? 'Missing required fields' : 'Add new suppliers')}
                  {step.key === 'profile' && (isIncomplete ? 'Missing required fields' : 'Add new contacts')}
                  {step.key === 'companies' && 'Manage contacts'}
                  {step.key === 'risk' && (
                    completedAnswers === 0
                      ? `${totalQuestions} questions to answer`
                      : completedAnswers < totalQuestions
                        ? `${completedAnswers}/${totalQuestions} answered`
                        : 'All questions answered'
                  )}
                </div>
              </div>
              {isActive && <ChevronRight className="h-5 w-5" />}
            </button>
          );
        })}
      </div>

      {/* Risk & Spend Summary */}
      {(annualSpend || riskLevel) && (
        <div className="mt-6 pt-4 border-t space-y-3">
          <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            <ShieldAlert className="h-3.5 w-3.5" />
            Risk Assessment
          </div>
          <div className="flex flex-wrap gap-2">
            {annualSpend && (
              <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
                Spend: {(() => {
                  // The band the user picked is quoted in the supplier's own
                  // currency (getAnnualSpendOptions), so this summary must use
                  // the same symbol — it hardcoded '£' and so showed pounds
                  // against an INR supplier.
                  const sym = CURRENCY_SYMBOLS[currency || ''] || '£';
                  const labels: Record<string, string> = {
                    under_10k: `< ${sym}10k`, '10k_50k': `${sym}10-50k`, '50k_100k': `${sym}50-100k`,
                    '100k_500k': `${sym}100-500k`, '500k_1m': `${sym}500k-1m`, over_1m: `> ${sym}1m`,
                  };
                  return labels[annualSpend] || annualSpend;
                })()}
              </Badge>
            )}
            {riskLevel && (
              <Badge className={
                riskLevel === 'high' ? 'bg-red-100 text-red-800 hover:bg-red-100' :
                riskLevel === 'medium' ? 'bg-amber-100 text-amber-800 hover:bg-amber-100' :
                'bg-green-100 text-green-800 hover:bg-green-100'
              }>
                Risk: {riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1)}
                {riskScore !== undefined && ` (${riskScore}%)`}
              </Badge>
            )}
          </div>
        </div>
      )}

    </div>
  );
}
